import { Injectable, Type } from '@angular/core';
import { Observable, Subject } from 'rxjs';
import { decodeJwt } from 'jose';
import { COMPONENTES_INTERNOS } from '../../../core/config/componentes-internos.config';
import { Modulo } from '../../../core/interfaces/modulo';
import {
  GrupoMenuFuncionalidade,
  ItemMenuFuncionalidade,
} from '../../../core/interfaces/menu-funcionalidade';
import { MenuItem } from '../../../core/interfaces/menu-item';
import { normalizarChave } from '../../utils/normalizarChave';
import { HomeMenuService } from '../home-menu/home-menu.service';
import { LocalStorageService } from '../localStorage/local-storage.service';

/** Grupo com acesso total, único caso em que o portal ainda decide permissão no cliente. */
const GRUPO_ADMIN = 'VIVEST_CENTER_DEV_ADMINS';
/** Chave opcional de localStorage para simular o grupo em teste, sem depender da claim. */
const CHAVE_GRUPO_LOCAL_STORAGE = 'grupoAcesso';

/**
 * Traduz a árvore de menus de `GET /v1/menus` para as duas telas: cards de
 * módulo (raízes) e menu lateral de funcionalidades (descendentes).
 *
 * Não há mais regra de permissão aqui. A API entrega apenas o que as claims do
 * token permitem ver, então "o módulo está na árvore" É a permissão — manter uma
 * segunda cópia da regra no cliente só daria um lugar a mais para divergir.
 */
@Injectable({
  providedIn: 'root',
})
export class AcessoModulosService {
  /**
   * Abrir um módulo pergunta três coisas à árvore (existe? posso? o que tem
   * dentro?). Sem este cache, seriam três chamadas HTTP para a mesma resposta.
   */
  private arvore?: Promise<MenuItem[]>;

  constructor(
    private homeMenuService: HomeMenuService,
    private localStorageService: LocalStorageService,
  ) {}

  /**
   * Avisa que a árvore foi invalidada, para quem já a estiver exibindo poder
   * buscá-la de novo — o menu lateral das funcionalidades, hoje.
   *
   * Sem isso, cadastrar um menu dentro do painel limpava o cache mas deixava
   * na tela o menu lateral antigo, e a mudança só aparecia no próximo acesso.
   */
  private readonly invalidacaoSubject = new Subject<void>();

  readonly arvoreInvalidada$: Observable<void> = this.invalidacaoSubject.asObservable();

  /**
   * Descarta o cache. A home chama ao entrar: é a porta do fluxo, e é lá que se
   * espera ver o efeito de um menu recém-cadastrado ou reparentado. As telas de
   * manutenção de menu chamam depois de gravar.
   */
  public recarregar(): void {
    this.arvore = undefined;
    this.invalidacaoSubject.next();
  }

  /**
   * O usuário logado está no grupo administrador?
   *
   * A árvore de menus já vem recortada pelas claims — isso não é usado para
   * decidir o que aparece na tela. É a única exceção pontual que sobrou no
   * cliente: o botão de editar o acesso ao Vivest Center, na Manutenção de
   * Usuários, é restrito ao administrador.
   */
  public isAdmin(): boolean {
    return this.getGruposUsuario().some(
      (grupo) => normalizarChave(grupo) === normalizarChave(GRUPO_ADMIN),
    );
  }

  /** Módulos que o usuário pode visualizar: as raízes da árvore. */
  async getModulosPermitidos(): Promise<Modulo[]> {
    const raizes = await this.getArvore();

    return raizes.map((raiz) => this.toModulo(raiz));
  }

  async getModuloPorId(moduloId?: string | null): Promise<Modulo | undefined> {
    const raiz = await this.getRaiz(moduloId);

    return raiz ? this.toModulo(raiz) : undefined;
  }

  /** A árvore já vem recortada pelas claims: estar nela é a permissão. */
  async podeAcessarModulo(moduloId?: string | null): Promise<boolean> {
    return !!(await this.getRaiz(moduloId));
  }

  /**
   * Menu lateral do módulo, com a mesma hierarquia que a Manutenção de Menus
   * mostra: um grupo por nó `GRUPO`, cada um dentro do grupo em que foi
   * cadastrado. As folhas presas direto na raiz formam o primeiro grupo, com o
   * nome do próprio módulo.
   */
  async getGruposDeFuncionalidades(
    moduloId?: string | null,
  ): Promise<GrupoMenuFuncionalidade[]> {
    const raiz = await this.getRaiz(moduloId);

    // Módulo em preparação não monta menu lateral: a segunda tela mostra só o
    // aviso de desenvolvimento, como já fazia.
    if (!raiz || this.toModulo(raiz).tipoConteudo !== 'funcionalidades') {
      return [];
    }

    return this.coletarGrupos(raiz);
  }

  /**
   * URL do microfrontend para abrir no iframe do painel. Devolve `null` para
   * menus de rota interna, que são resolvidos por `getComponenteInterno`.
   */
  async getUrlDaFuncionalidade(
    menu: MenuItem,
  ): Promise<{ url: string; abrirEmNovaAba: boolean } | null> {
    if (menu.targetType === 'EXTERNAL_URL') {
      // EXTERNAL_URL não tem endpoint de launch: usa o target direto.
      return { url: menu.target, abrirEmNovaAba: menu.openInNewTab };
    }

    if (menu.targetType === 'LAUNCH') {
      const launch = await this.homeMenuService.launchMenu(menu.id);

      return { url: launch.launchUrl, abrirEmNovaAba: launch.openInNewTab };
    }

    return null;
  }

  /**
   * Componente do portal correspondente à rota do menu, para ser exibido no
   * painel sem iframe. Devolve `null` quando a rota não está mapeada em
   * COMPONENTES_INTERNOS — nesse caso a tela navega normalmente pelo router.
   */
  async getComponenteInterno(target?: string | null): Promise<Type<unknown> | null> {
    const chave = normalizarChave(target?.split('?')[0]);
    const carregar = COMPONENTES_INTERNOS[chave];

    if (!carregar) {
      return null;
    }

    try {
      return await carregar();
    } catch (error) {
      console.error('Não foi possível carregar a tela interna:', error);
      return null;
    }
  }

  private getArvore(): Promise<MenuItem[]> {
    if (!this.arvore) {
      // Falha não fica grudada no cache: a próxima tentativa bate na API de novo,
      // senão um 401 momentâneo deixaria a sessão inteira sem menu.
      this.arvore = this.homeMenuService
        .getHomeMenus()
        .then((menus) => this.podarInativos(menus))
        .catch((error) => {
          this.arvore = undefined;
          throw error;
        });
    }

    return this.arvore;
  }

  /**
   * Tira da árvore o que está inativo, com o ramo inteiro.
   *
   * A poda fica aqui, na única porta de entrada da árvore, e não em cada tela:
   * assim os cards da home, o menu lateral de funcionalidades e a checagem de
   * acesso ao módulo enxergam a mesma grade. "Estar na árvore" continua sendo
   * a permissão — agora estar no ar também é.
   *
   * Grupo inativo leva os filhos junto: um item alcançável por dentro de um
   * grupo que ninguém vê não deveria aparecer por fora dele.
   */
  private podarInativos(menus: MenuItem[]): MenuItem[] {
    let mudou = false;
    const podados: MenuItem[] = [];

    for (const menu of menus) {
      if (menu.isActive === false) {
        mudou = true;
        continue;
      }

      const originais = menu.children ?? [];
      const filhos = this.podarInativos(originais);

      // Nó sem poda dentro segue sendo o mesmo objeto. Copiar tudo por
      // precaução quebraria quem compara `MenuItem` por referência — o painel
      // entrega o próprio item ao host do microfrontend.
      if (filhos === originais) {
        podados.push(menu);
        continue;
      }

      mudou = true;
      podados.push({ ...menu, children: filhos });
    }

    return mudou ? podados : menus;
  }

  private async getRaiz(moduloId?: string | null): Promise<MenuItem | undefined> {
    const chave = normalizarChave(moduloId);

    if (!chave) {
      return undefined;
    }

    const raizes = await this.getArvore();

    return raizes.find((raiz) => normalizarChave(raiz.slug) === chave);
  }

  private toModulo(raiz: MenuItem): Modulo {
    return {
      id: raiz.slug,
      titulo: raiz.label,
      descricao: raiz.description ?? '',
      // Grupo sem nada dentro é módulo em preparação, e a segunda tela mostra o
      // aviso de desenvolvimento em vez de um menu lateral vazio. Raiz-folha tem
      // conteúdo próprio, então sempre abre.
      tipoConteudo:
        raiz.itemType !== 'GRUPO' || raiz.children.length > 0
          ? 'funcionalidades'
          : 'em-desenvolvimento',
      rota: `/modulos/${raiz.slug}`,
      iconeClasse: raiz.icon ?? undefined,
      cor: raiz.color ?? undefined,
    };
  }

  /**
   * Seções do primeiro nível do menu lateral, a partir da raiz do módulo.
   *
   * As folhas presas direto na raiz formam uma seção com o nome do próprio
   * módulo, e cada subgrupo da raiz vira uma seção ao lado dela — levando junto
   * os próprios subgrupos, em quantos níveis a árvore tiver. A raiz não vira um
   * nível a mais porque o nome do módulo já está no cabeçalho da tela.
   *
   * Raiz que só contém subgrupos não ganha seção própria: sobraria um título
   * repetindo o cabeçalho.
   */
  private coletarGrupos(no: MenuItem): GrupoMenuFuncionalidade[] {
    // Raiz-folha (menu sem pai, como os LAUNCH que ainda não foram para um
    // grupo): vira um menu de um item só, para o painel abrir o conteúdo dele.
    if (no.itemType !== 'GRUPO') {
      return [this.toGrupo(no, [this.toItem(no)], [])];
    }

    const folhas = this.folhasDe(no);
    const subgrupos = this.subgruposDe(no);
    const propria =
      folhas.length > 0 || subgrupos.length === 0 ? [this.toGrupo(no, folhas, [])] : [];

    return [...propria, ...subgrupos];
  }

  /**
   * Um nó `GRUPO` com tudo o que está pendurado nele, na profundidade em que
   * foi cadastrado: as folhas ficam no grupo onde estão e os subgrupos
   * continuam dentro do pai deles.
   *
   * Antes a árvore era achatada num nível só de seções, e o menu lateral
   * mostrava subgrupo e avô como irmãos — além de perder pelo caminho o nome do
   * grupo que só carrega subgrupos.
   *
   * Grupo sem nada dentro é mantido de propósito: é o que a tela mostra como
   * "em desenvolvimento".
   */
  private montarGrupo(no: MenuItem): GrupoMenuFuncionalidade {
    return this.toGrupo(no, this.folhasDe(no), this.subgruposDe(no));
  }

  /** Funcionalidades presas diretamente no nó, na ordem da árvore. */
  private folhasDe(no: MenuItem): ItemMenuFuncionalidade[] {
    return no.children
      .filter((filho) => filho.itemType !== 'GRUPO')
      .map((folha) => this.toItem(folha));
  }

  /** Grupos que moram dentro do nó, cada um já com o próprio conteúdo. */
  private subgruposDe(no: MenuItem): GrupoMenuFuncionalidade[] {
    return no.children
      .filter((filho) => filho.itemType === 'GRUPO')
      .map((subgrupo) => this.montarGrupo(subgrupo));
  }

  private toGrupo(
    no: MenuItem,
    itens: ItemMenuFuncionalidade[],
    subgrupos: GrupoMenuFuncionalidade[],
  ): GrupoMenuFuncionalidade {
    return { id: no.slug, titulo: no.label, origem: 'api', itens, subgrupos };
  }

  private toItem(menu: MenuItem): ItemMenuFuncionalidade {
    return { id: `menu-${menu.id}`, titulo: menu.label, menu };
  }

  /** Grupo do localStorage (teste), senão a claim `role` do token. */
  private getGruposUsuario(): string[] {
    const grupoLocal = this.localStorageService.get(CHAVE_GRUPO_LOCAL_STORAGE);

    if (grupoLocal) {
      return this.normalizarGrupos(grupoLocal);
    }

    return this.getGruposDoToken();
  }

  private getGruposDoToken(): string[] {
    const token = String(this.localStorageService.get('bearerToken') ?? '').trim();

    if (!token) {
      return [];
    }

    try {
      const claims = decodeJwt(token) as Record<string, unknown>;
      return this.normalizarGrupos(claims['role']);
    } catch (error) {
      console.error('Não foi possível ler os grupos do usuário no token:', error);
      return [];
    }
  }

  /**
   * A claim `role` traz um único grupo ou a lista de grupos do usuário, então
   * os dois formatos são aceitos. Texto com vários grupos pode vir separado por
   * vírgula ou ponto e vírgula.
   */
  private normalizarGrupos(valorClaim: unknown): string[] {
    const valores = Array.isArray(valorClaim)
      ? valorClaim
      : String(valorClaim ?? '').split(/[,;]/);

    return [
      ...new Set(
        valores
          .filter((valor): valor is string => typeof valor === 'string')
          .map((valor) => valor.trim())
          .filter((valor) => valor.length > 0),
      ),
    ];
  }
}
